import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { AuthorizationService, RefundData } from 'src/app/services/authorization/authorization.service';
import { Authorization } from 'src/app/model/Authorization';
import { ConfirmationDialogComponent, ConfirmationDialogData } from 'src/app/components/confirmation-dialog/confirmation-dialog.component';

/** Dialog data provided to Refund Authorization Dialog */
export interface RefundAuthorizationDialogData {
  authorization: Authorization;
}

@Component({
    selector: 'app-refund-authorization-dialog',
    templateUrl: './refund-authorization-dialog.component.html',
    styleUrl: './refund-authorization-dialog.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class RefundAuthorizationDialogComponent {
  /** Dependency injections */
  private readonly _dialogRef: MatDialogRef<RefundAuthorizationDialogComponent> = inject(MatDialogRef);
  private readonly _authorizationService: AuthorizationService = inject(AuthorizationService);
  private readonly _toastrService: ToastrService = inject(ToastrService);
    private readonly _dialog: MatDialog = inject(MatDialog); 

  /** Dialog data */
  public data: RefundAuthorizationDialogData = inject(MAT_DIALOG_DATA);

  /** Dynamic form variables */
  refundForm: FormGroup;
  isSubmitting = false;

  /**
   * On construct, initializes the refund form
   */
  constructor() {
    this.refundForm = new FormGroup({
      // amount: new FormControl('', [
      //   Validators.required,
      //   Validators.min(0.01),
      //   Validators.max(this.data.authorization.captured_amount),
      //   Validators.pattern(/^\d+(\.\d{1,2})?$/) // Allows numbers with up to 2 decimals
      // ]),
      memo: new FormControl('')
    });
  }

  /**
   * Handle refund form submission
   */
public submit(): void {
  // Validate form first
  if (this.refundForm.invalid) {
    this._toastrService.error('Please fill in all required fields correctly');
    return;
  }

  const formValues = this.refundForm.value;
  const amount = formValues.amount;
  const authId = this.data.authorization.id;

  const dialogRef = this._dialog.open<ConfirmationDialogComponent, ConfirmationDialogData, boolean>(
    ConfirmationDialogComponent,
    {
      width: '400px',
      disableClose: true,
      data: {
        title: 'Confirm Refund',
        message: `Are you sure you want to refund the full captured amount?`,
        actions: { align: 'end' },
        confirm: { text: 'Refund', color: 'warn' },
        cancel: { text: 'Cancel', color: '' }
      }
    }
  );

  dialogRef.afterClosed().subscribe((confirmed) => {
    if (confirmed) {
      this.doRefund();   // 👈 only run refund if user clicked confirm
    }
  });
}

private doRefund(): void {
  this.isSubmitting = true;

  const formValues = this.refundForm.value;
  const refundData: RefundData = {
    // amount: Number(formValues.amount),
    memo: formValues.memo || '',
  };

  try {
    this._authorizationService.refundAuthorization(this.data.authorization.id, refundData)
      .pipe(finalize(() => setTimeout(() => (this.isSubmitting = false), 1000)))
      .subscribe({
        next: () => {
          this._toastrService.success('Refund processed successfully');
          this.close(true);
        },
        error: (err: any) => {
          console.error('[REFUND-AUTHORIZATION-DIALOG-COMPONENT]: Error processing refund', err);
          this._toastrService.error('Failed to process refund');
        }
      });
  } catch (err: any) {
    console.error('[REFUND-AUTHORIZATION-DIALOG-COMPONENT]: Error processing refund:', err);
    this._toastrService.error('Failed to create refund');
    this.isSubmitting = false;
  }
}


  /**
   * Close the dialog
   */
  public close(success: boolean = false): void {
    this._dialogRef.close({ success });
  }
}